/**
 * Two-column context component.
 *
 * Displays context content in two equal-width columns with a vertical separator.
 * Used for special cases where users need to compare two related texts side-by-side.
 *
 * @module TwoColumnContext
 */

import { useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface TwoColumnContextProps {
  readonly leftContent: string;
  readonly rightContent: string;
  readonly textSizeClass: string;
  readonly isHighlighted: boolean;
  readonly highlighterActive: boolean;
  readonly leftQuestionNumbers: number[];
  readonly rightQuestionNumbers: number[];
  readonly paragraphId: number;
  readonly leftHighlights: { start: number; end: number }[];
  readonly rightHighlights: { start: number; end: number }[];
  readonly onAddHighlight: (paragraphId: number, start: number, end: number, column: 'left' | 'right') => void;
  readonly onQuestionBadgeClick?: (questionId: number) => void;
  readonly activeQuestionId?: number | null;
}

/**
 * Renders text content with highlight marks applied.
 * Splits the content into segments based on highlight ranges.
 */
function renderHighlightedText(
  content: string,
  highlights: { start: number; end: number }[]
): React.ReactNode {
  if (highlights.length === 0) {
    return content;
  }

  // Sort highlights by start position
  const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);

  const segments: React.ReactNode[] = [];
  let currentIndex = 0;

  for (const highlight of sortedHighlights) {
    // Add non-highlighted text before this highlight
    if (highlight.start > currentIndex) {
      segments.push(
        <span key={`text-${currentIndex}`}>
          {content.slice(currentIndex, highlight.start)}
        </span>
      );
    }

    // Add highlighted text
    const highlightStart = Math.max(highlight.start, currentIndex);
    const highlightEnd = Math.min(highlight.end, content.length);
    if (highlightEnd > highlightStart) {
      segments.push(
        <mark
          key={`highlight-${highlightStart}`}
          className="bg-yellow-300/60 dark:bg-yellow-500/40 px-0.5 rounded-sm"
        >
          {content.slice(highlightStart, highlightEnd)}
        </mark>
      );
    }

    currentIndex = Math.max(currentIndex, highlightEnd);
  }

  // Add remaining non-highlighted text
  if (currentIndex < content.length) {
    segments.push(
      <span key={`text-${currentIndex}`}>
        {content.slice(currentIndex)}
      </span>
    );
  }

  return <>{segments}</>;
}

export function TwoColumnContext({
  leftContent,
  rightContent,
  textSizeClass,
  isHighlighted,
  highlighterActive,
  leftQuestionNumbers,
  rightQuestionNumbers,
  paragraphId,
  leftHighlights,
  rightHighlights,
  onAddHighlight,
  onQuestionBadgeClick,
  activeQuestionId,
}: TwoColumnContextProps) {
  const leftRef = useRef<HTMLParagraphElement>(null);
  const rightRef = useRef<HTMLParagraphElement>(null);

  /**
   * Handle text selection when highlighter is active.
   */
  const handleMouseUp = useCallback((column: 'left' | 'right', content: string) => {
    if (!highlighterActive) {
      return;
    }

    const selection = globalThis.getSelection();
    if (!selection || selection.isCollapsed) {
      return;
    }

    const ref = column === 'left' ? leftRef.current : rightRef.current;
    if (!ref) {
      return;
    }

    // Check if selection is within this column
    const range = selection.getRangeAt(0);
    if (!ref.contains(range.commonAncestorContainer)) {
      return;
    }

    const selectedText = selection.toString();
    if (!selectedText.trim()) {
      return;
    }

    // Calculate offsets based on the DOM structure
    const preCaretRange = document.createRange();
    preCaretRange.selectNodeContents(ref);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const start = preCaretRange.toString().length;
    const end = start + selectedText.length;

    // Validate offsets are within bounds
    if (start >= 0 && end <= content.length && start < end) {
      onAddHighlight(paragraphId, start, end, column);
    }

    // Clear selection after highlighting
    selection.removeAllRanges();
  }, [highlighterActive, paragraphId, onAddHighlight]);

  useEffect(() => {
    // We use manual event listeners instead of onMouseUp props to satisfy accessibility linting.
    const leftEl = leftRef.current;
    const rightEl = rightRef.current;
    if (!highlighterActive) return;

    const onLeftMouseUp = () => handleMouseUp('left', leftContent);
    const onRightMouseUp = () => handleMouseUp('right', rightContent);

    leftEl?.addEventListener('mouseup', onLeftMouseUp);
    rightEl?.addEventListener('mouseup', onRightMouseUp);

    return () => {
      leftEl?.removeEventListener('mouseup', onLeftMouseUp);
      rightEl?.removeEventListener('mouseup', onRightMouseUp);
    };
  }, [highlighterActive, handleMouseUp, leftContent, rightContent]);

  const renderQuestionBadges = (numbers: number[]) =>
    numbers.map((qNum) => {
      const badgeClass = cn(
        "w-5 h-5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold transition-all shadow-sm shrink-0",
        "bg-primary text-primary-foreground border border-primary/20",
        "hover:scale-110",
        onQuestionBadgeClick
          ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          : "cursor-default"
      );
      if (onQuestionBadgeClick) {
        return (
          <button
            key={qNum}
            type="button"
            className={cn(badgeClass, "relative before:absolute before:content-[''] before:-inset-3 before:rounded-full")}
            aria-label={`Go to question ${qNum}`}
            aria-current={activeQuestionId === qNum ? 'true' : undefined}
            onClick={(e) => {
              e.stopPropagation();
              onQuestionBadgeClick(qNum);
            }}
          >
            {qNum}
          </button>
        );
      }
      return (
        <span key={qNum} className={badgeClass}>
          {qNum}
        </span>
      );
    });

  return (
    <div
      className={cn(
        "relative rounded-lg transition-all duration-500",
        isHighlighted && "ring-2 ring-primary ring-offset-2",
        highlighterActive && "cursor-text select-text"
      )}
    >
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
        {/* Left Column */}
        <div className="flex-1 relative">
          {/* Question Number Badges */}
          {leftQuestionNumbers.length > 0 && (
            <div className="absolute -left-6 sm:-left-12 top-1 flex flex-col gap-1.5 z-10">
              {renderQuestionBadges(leftQuestionNumbers)}
            </div>
          )}

          <div
            className={cn(
              "text-foreground h-full p-3 sm:p-0 border sm:border-0 border-border/40 rounded-lg bg-card/10 sm:bg-transparent",
              textSizeClass
            )}
          >
            <p
              ref={leftRef}
              className="whitespace-pre-line leading-relaxed"
            >
              {renderHighlightedText(leftContent, leftHighlights)}
            </p>
          </div>
        </div>

        {/* Vertical Separator - Hidden on mobile, shown on desktop */}
        <div className="hidden sm:block w-px bg-border/60 shrink-0" />

        {/* Right Column */}
        <div className="flex-1 relative">
          {/* Question Number Badges */}
          {rightQuestionNumbers.length > 0 && (
            <div className="absolute -left-6 sm:-left-12 top-1 flex flex-col gap-1.5 z-10">
              {renderQuestionBadges(rightQuestionNumbers)}
            </div>
          )}

          <div
            className={cn(
              "text-foreground h-full p-3 sm:p-0 border sm:border-0 border-border/40 rounded-lg bg-card/10 sm:bg-transparent",
              textSizeClass
            )}
          >
            <p
              ref={rightRef}
              className="whitespace-pre-line leading-relaxed"
            >
              {renderHighlightedText(rightContent, rightHighlights)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
